import { Injectable } from '@angular/core';
import { ContentAuthorizationData } from 'fl-content-authorization';
import {
  createHeartbeatBuilder,
  HeartbeatBuilder,
  HeartbeatConfiguration,
} from 'fl-heartbeat';
import { createComposablePlayer } from 'fl-player';
import {
  ComposablePlayer,
  Player,
  RequestType,
  TrackInfo,
  TrackType,
} from 'fl-player-interface';
import {
  createStreamConcurrencyResolver,
  StreamConcurrencyConfiguration,
} from 'fl-stream-concurrency';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { DetailPageService } from '../detail-page/services/detail-page.service';
import {
  AQ_ATMOS,
  AQ_DOLBY,
  AUDIO_DOLBY,
  dolbyAtmosQualities,
  dolbyAudioQualities,
} from '../shared/common/const/common.const';
import {
  IPlayer,
  IPlayerSkipInfo,
} from '../shared/common/interfaces/common.interface';
import { ContentType } from '../shared/enums/contentType';
import { PlayerState } from '../shared/enums/player-states';
import {
  EntitlementStatus,
  SubscriptionAction,
  SubscriptionStatus,
} from '../shared/enums/subscription';
import { AuthService } from '../shared/services/auth/auth.service';
import { BookmarkService } from '../shared/services/bookmark.service';
import { ConfigService } from '../shared/services/config.service';
import { DeviceHandlerService } from '../shared/services/device-handler.service';
import { AccountProfileService } from '../shared/services/evergent/account/account-profile.service';
import { ContentEntitlementService } from '../shared/services/evergent/entitlement/content-entitlement.service';
import { UserEntitlementService } from '../shared/services/evergent/entitlement/user-entitlement.service';
import { RecommendationsService } from '../shared/services/firstlight/recommendations/recommendations.service';
import { PlaybackService } from '../shared/services/playback.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';
import { KEYS } from '../shared/utils/constants/storage.constants';
import {
  getPreferredAudioLanguage,
  getTextTrackList,
} from '../shared/utils/PlaybackUtils';
import {
  isDeviceBelow2020,
  shouldDisableAds,
  shouldDisableKeyframes,
} from '../shared/utils/playerUtils';
import { StorefrontService } from '../storefront/storefront.service';
import { OFF } from '../shared/utils/deviceUtils';

// Define the combined state interface
export interface AppPlayerNetworkState {
  playerState: PlayerState;
  networkState: 'online' | 'offline';
  appState?: 'foreground' | 'background';
}

@Injectable({
  providedIn: 'root',
})
export class PlayerService {
  playerState$ = new Subject<AppPlayerNetworkState>();

  preferredAudio: IPlayer = null;
  preferredVideo: IPlayer = null;
  preferredSubtitles: IPlayer = null;
  private language = {
    en: 'English',
    te: 'Telugu',
    ta: 'Tamil',
  };
  audioLanguage: string | undefined = undefined;
  subtitleLanguage: string = OFF;

  constructor(
    private readonly configService: ConfigService,
    private readonly authService: AuthService,
    private readonly bookmarkService: BookmarkService,
    private readonly deviceHandlerService: DeviceHandlerService,
    private readonly localStorageService: LocalStorageService,
    private readonly recommendationService: RecommendationsService,
    private readonly storefrontService: StorefrontService,
    private readonly detailPageService: DetailPageService,
    private readonly contentEntitlementService: ContentEntitlementService,
    private readonly userEntitlementService: UserEntitlementService,
    private readonly playbackService: PlaybackService,
    private readonly userEntlmnt: UserEntitlementService,
    private readonly accountProfileService: AccountProfileService
  ) {}

  get videoElement(): HTMLVideoElement {
    const element = document.getElementById('detail-video-player');
    return element as HTMLVideoElement;
  }

  get videoContainer(): HTMLElement {
    const element = document.getElementById('detail-video-container');
    return element as HTMLElement;
  }

  /* get adsVideoContainer(): HTMLElement {
    const element = document.getElementById('add-container');
    return element as HTMLElement;
  } */

  /*   removeAdsVideoContainer(): void {
    const element = document.getElementById('add-container');
    if (element) {
      element.remove();
    }
  } */

  needsBookmark(metadataType: string, resource: ResourceVm) {
    return !(
      metadataType === ContentType.LIVE_EVENT ||
      metadataType === ContentType.LIVE_CHANNEL ||
      metadataType === ContentType.CHANNEL ||
      metadataType === ContentType.TRAILER ||
      metadataType === ContentType.SHORT_VIDEO ||
      this.contentEntitlementService.canPlayVideo(resource) ===
        SubscriptionStatus.FREE_PREVIEW
    );
  }

  initBookmarkSession(
    resource: ResourceVm,
    uniqueId: string,
    metadataType: string,
    playerInstance: Player
  ): void {
    const canBookmark =
      this.authService.isLoggedIn() &&
      this.needsBookmark(metadataType, resource);
    if (canBookmark) {
      const isEpisode = resource.contentType.includes('episode');
      const bookmarker = this.bookmarkService.bookmarkMarker({
        contentId: isEpisode ? resource.seriesId : uniqueId,
        seasonId: resource?.seasonId,
        episodeId: isEpisode ? resource.id : undefined,
        platformAuthorizer: this.authService.platformAuthorizer,
      });
      const composablePlayer = createComposablePlayer(playerInstance);
      composablePlayer.addStateChangeStep((player) =>
        bookmarker.processPlayerStateChange(player)
      );
    }
  }

  setInitialAudioLanguage(resource: ResourceVm): void {
    const code = getPreferredAudioLanguage(
      resource,
      this.authService.isLoggedIn()
    );
    this.audioLanguage = this.language[code] || this.language['te'];
  }

  initConcurrencyTracker(playerInstance: ComposablePlayer) {
    try {
      if (!this.authService.isLoggedIn()) {
        return;
      }
      const streamConcurrencyConfig: StreamConcurrencyConfiguration = {
        endPointUrl: this.configService.applicationConfig.strConcurrencyURL,
        syncIntervalMS: parseInt(
          this.configService.applicationConfig.streamConcurrencySyncIntervalMs,
          10
        ),
      };

      const concurrencyResolver = createStreamConcurrencyResolver(
        this.deviceHandlerService.getCurrentDeviceID(),
        streamConcurrencyConfig,
        this.authService.platformAuthorizer
      );
      playerInstance.addStateChangeStep((player) =>
        concurrencyResolver.processPlayerStateChange(player)
      );
    } catch (e) {
      console.error('Error while initializing concurrency tracker', e);
    }
  }

  initHeartbeat(
    data: ContentAuthorizationData,
    uniqueId: string,
    composablePlayer: ComposablePlayer
  ) {
    if (!this.authService.isLoggedIn() || !data.heartbeatFlag) {
      return;
    }
    // Heartbeat config
    const heartbeatConfiguration: HeartbeatConfiguration = {
      heartbeatEndPointUrl: this.configService.applicationConfig.heartBeatURL,
      streamConcurrencyEndPointUrl:
        this.configService.applicationConfig.strConcurrencyURL,
      syncIntervalMS: parseInt(
        this.configService.applicationConfig.heartBeatSyncInterval,
        10
      ),
    };

    // Building Heartbeat
    const heartbeatBuilder: HeartbeatBuilder = createHeartbeatBuilder(
      this.deviceHandlerService.getCurrentDeviceID(),
      uniqueId,
      heartbeatConfiguration,
      this.authService.platformAuthorizer
    );
    const heartbeatManager = heartbeatBuilder
      .setHeartbeatToken(data.heartbeatToken)
      // .setLiveEndTime(authorizationResponse.liveEndTime)
      // .setLiveStartTime(authorizationResponse.liveStartTime)
      .build();

    // Attaches heartbeat to the player
    composablePlayer.addStateChangeStep((player: Player) =>
      heartbeatManager.processPlayerStateChange(player)
    );
    composablePlayer.addHeartBeatStep((player: Player) =>
      heartbeatManager.processHeartbeatChange(player)
    );
  }

  audioQualityOptions(player: Player): TrackInfo[] {
    return player?.getAllTracks().filter((it) => it.type === TrackType.AUDIO);
  }

  audioQualities(player: Player, resource: ResourceVm) {
    let hasDolbyAtmos = false;
    let hasDolbyAudio = false;
    const activeAudioTrack = player
      .getActiveTracks()
      ?.filter((it) => it.type === TrackType.AUDIO);

    const list: IPlayer[] = this.audioQualityOptions(player)?.map(
      (it: TrackInfo, index: number) => {
        const isDolby = it.codecs.includes(AUDIO_DOLBY);
        hasDolbyAudio = isDolby && !it.spatialAudio;
        hasDolbyAtmos = isDolby && it.spatialAudio;
        const keyValue = index + 1;
        return {
          key: keyValue,
          name:
            it.language === 'en'
              ? this.language[resource.inLanguage]
              : this.getAudioName(it) || this.language['te'],
          isUpsell: false,
          isSelected: false, // will set below
          isOriginal:
            (it.language === 'en' || resource.inLanguage === it.language) &&
            !(hasDolbyAtmos || hasDolbyAudio),
          track: it,
          isDolbyAtmos: hasDolbyAtmos,
          isDolbyAudio: hasDolbyAudio,
        };
      }
    );
    // Set the highest available audio quality as selected by default if no preferredAudio
    if (!this.preferredAudio) {
      if (activeAudioTrack?.length) {
        list.forEach((item) => {
          item.isSelected =
            item.track.codecs === activeAudioTrack[0].codecs &&
            item.track.language === activeAudioTrack[0].language &&
            item.track.spatialAudio === activeAudioTrack[0].spatialAudio;
          if (item.isSelected) {
            this.audioLanguage = item.name;
          }
        });
      }
    } else {
      // fallback to previous logic for preferredAudio
      list.forEach((item) => {
        item.isSelected = this.preferredAudio.key === item.key;
        if (item.isSelected) {
          this.audioLanguage = item.name;
        }
      });
    }
    return list;
  }

  videoQualities(player: Player, resource?: ResourceVm) {
    const videoTypeList = player.getAvailableTrackVariants(TrackType.VIDEO);
    const qualityMap = [
      { min: 0, max: 479, list: [], label: 'Auto', value: '' },
      { min: 480, max: 719, list: [], label: 'SD', value: '480p' },
      { min: 720, max: 1079, list: [], label: 'HD', value: '720p' },
      {
        min: 1080,
        max: 2159,
        list: [],
        label: 'Full HD',
        value: '1080p',
      },
      {
        min: 2160,
        max: Infinity,
        list: [],
        label: '4K UHD',
        value: '2160p',
      },
    ];

    videoTypeList.forEach((videotype) => {
      for (const { min, max, list } of qualityMap) {
        if (videotype.height >= min && videotype.height < max) {
          list.push(videotype);
          break;
        }
      }
    });

    const videoQualityList = qualityMap
      .filter(({ list }) => list.length > 0)
      .map(
        ({ label, value, list }, index) =>
          ({
            key: index + 1,
            name: label,
            value,
            isSelected: this.preferredVideo
              ? this.preferredVideo.key === index + 1
              : index === 0,
            bandwidth: list.reduce(
              (max, track) => Math.max(max, track.bandwidth || 0),
              0
            ),
          }) as IPlayer
      );

    // Add 4K upsell if content is 4K, device supports 4K, and user is unsubscribed
    if (resource && this.shouldShow4kUpsell(resource)) {
      const has4kQuality = videoQualityList.some(
        (quality) => quality.value === '2160p'
      );
      if (!has4kQuality) {
        videoQualityList.push({
          key: videoQualityList.length + 1,
          name: '4K UHD',
          value: '2160p',
          isUpsell: true,
          isSelected: false,
          bandwidth: 0,
        });
      }
    }

    return videoQualityList;
  }

  private shouldShow4kUpsell(resource: ResourceVm): boolean {
    // Check if content is 4K
    const is4kContent = resource.is4k;

    // Check if device supports 4K
    const deviceSupports4k = Boolean(window.support4k);

    // Check if user needs to upgrade to see 4K content
    let action: string | null = null;
    // Avoid memory leak by using take(1) to auto-unsubscribe after first emission
    this.userEntitlementService.planActionBtn$
      .pipe(take(1))
      .subscribe((val) => {
        action = val;
      });
    const needsUpgrade = action === SubscriptionAction.UPGRADE;

    return (
      is4kContent &&
      deviceSupports4k &&
      (needsUpgrade || this.isUserEligibleToDisplayContentQuality())
    );
  }

  private isUserEligibleToDisplayContentQuality(): boolean {
    const userLoggedIn = this.authService.isLoggedIn();
    const subscription = this.userEntlmnt.subscription;
    const account =
      this.accountProfileService.account?.GetAccountProfileResponseMessage;
    const isSubscribed =
      Boolean(account?.subscriptionStatus) && Boolean(subscription);
    const isChurnedUser = subscription?.status == EntitlementStatus.FINAL_BILL;
    return !userLoggedIn || isChurnedUser || !isSubscribed;
  }

  cleanUpPreference() {
    this.preferredAudio = null;
    this.preferredVideo = null;
    this.preferredSubtitles = null;
    this.audioLanguage = undefined;
  }

  getSubtitlesList(player: Player) {
    const tracks = getTextTrackList(player);
    return tracks?.length ? tracks : null;
  }

  setSubtitles(player: Player, data: IPlayer) {
    this.localStorageService.setItem(KEYS.SUBTITLE, JSON.stringify(data));
    if (data.value === OFF.toLowerCase()) {
      player.selectTrack();
    } else {
      const track = player
        .getAllTracks()
        .find((it) => it.type === 'text' && it.language === data.value);
      if (track) {
        player.selectTrack(track);
      }
    }
  }

  setAudioQuality(player: Player, data: IPlayer) {
    // const currentTime = player.currentTime;
    // this.localStorageService.setItem(
    //   KEYS.CHANGE_AUDIO,
    //   JSON.stringify({
    //     currentTime,
    //     currentAudioQuality: data.value,
    //   })
    // );
    player.selectTrack(data.track);
    // this._reloadPlayer.next(true);
  }

  setVideoQuality(player: Player, value: IPlayer) {
    const videoQualities = this.videoQualities(player);
    videoQualities.forEach((it) => {
      if (it.key === value.key) {
        it.isSelected = true;
      } else {
        it.isSelected = false;
      }
    });
    player.maxBitrate = value.bandwidth;
    this.preferredVideo = value;
  }

  show4kUpsell(resource: ResourceVm) {
    return resource.is4k && Boolean(window.support4k);
  }

  private isDolbyAtmos(resource: ResourceVm) {
    return dolbyAtmosQualities
      .map((it) => it.toLowerCase())
      .includes(resource.audioQuality.toLowerCase());
  }

  upsellDolbyAudio(resource: ResourceVm) {
    const isDolby51 = dolbyAudioQualities
      .map((it) => it.toLowerCase())
      .includes(resource.audioQuality.toLowerCase());
    const isDolbyAtmos = this.isDolbyAtmos(resource);
    const deviceAudio = window.xSupportedAudio;
    const deviceSupport = deviceAudio?.includes('dolbyAudio');
    return (isDolby51 || isDolbyAtmos) && deviceSupport;
  }

  setRequestInterceptor(player: Player) {
    player.setRequestInterceptor(function (type, request) {
      if (type == RequestType.SEGMENT && request.method === 'HEAD') {
        request.method = 'GET';
      }
    });
  }

  private getAudioName(value: TrackInfo) {
    return this.language[value.language];
  }

  onSkipIntro(player: Player, skipIntro: IPlayerSkipInfo) {
    player.seek(skipIntro.end);
  }

  async nextContents(resource: ResourceVm) {
    if (resource.contentType === ContentType.MOVIE) {
      return await this.fetchRelatedItems(resource);
    } else {
      const result = await this.fetchUpNext(resource);
      if (!result?.upNext) {
        return await this.fetchRelatedItems(resource);
      } else {
        return result;
      }
    }
  }

  canFetchNextContents(resource: ResourceVm) {
    const contentType = resource.contentType;
    return ![
      ContentType.CHANNEL,
      ContentType.LIVE_CHANNEL,
      ContentType.LIVE_EVENT,
      ContentType.TRAILER,
      ContentType.SHORT_VIDEO,
      ContentType.PROMO,
      ContentType.EVENT,
    ].includes(contentType as ContentType);
  }

  private async fetchRelatedItems(resource: ResourceVm) {
    const items = await this.recommendationService.fetchMoreLikeThis(
      resource.id,
      resource.contentType,
      '16x9'
    );
    if (items && items?.resources?.length > 0) {
      return {
        relatedItems: {
          id: '',
          name: 'recommended_for_you',
          layout: 'carousel',
          aspectRatio: '16x9',
          contentCount: items.resources.length,
          viewAll: false,
          size: 'regular',
          style: 'regular',
          resources: items.resources,
        } as ContainerVm,
      };
    }
    return null;
  }

  private async fetchUpNext(resource: ResourceVm) {
    const upNext = await this.storefrontService.fetchUpNextContent(
      resource.seriesId,
      resource.contentGenre,
      resource.contentType,
      resource.providerName,
      resource.seasonNumber,
      resource.episodeNumber
    );
    if (upNext?.seriesId !== resource.seriesId) {
      return null;
    } else {
      return {
        upNext,
      };
    }
  }

  async fetchEpisodes(
    resource: ResourceVm,
    episodePageNumber: number = 1,
    episodePageSize: number = 20
  ) {
    const result = await this.detailPageService.fetchEpisodes(
      resource?.seriesId,
      resource.seasonId,
      resource.seasonNumber,
      episodePageNumber,
      episodePageSize
    );
    if (result) {
      result.name = 'episodes-list';
      return result;
    }
    return result;
  }

  async spriteUrl(
    player: Player,
    contentAuth: ContentAuthorizationData
  ): Promise<string[]> {
    try {
      // Get thumbnail at time 0 to extract the actual URL pattern
      const result = await player.getThumbnail(0);

      if (!result || !result.urls || result.urls.length === 0) {
        console.warn('No thumbnail URL found at time 0');
        return [];
      }

      const firstUrl = result.urls[0];
      const keyframe = contentAuth?.keyframe;

      if (!keyframe || !keyframe.ia || keyframe.ia.length === 0) {
        console.warn('No keyframe data or ia array found');
        return [];
      }

      // Extract the base URL pattern from the first thumbnail URL
      // Example URL: https://imageResizeURL/image/contentId/1737725222159-sprite-384x216-1.jpg?width=3840&height=2160
      const urlParts = firstUrl.split('?');
      const baseUrl = urlParts[0]; // URL without query params
      const queryParams = urlParts[1] || ''; // Query params (width=...&height=...)

      // Extract the base path (everything before the filename)
      // Example: "https://imageResizeURL/image/contentId/1737725222159-sprite-384x216-1.jpg"
      // -> "https://imageResizeURL/image/contentId/"
      const lastSlashIndex = baseUrl.lastIndexOf('/');
      const basePath = baseUrl.substring(0, lastSlashIndex + 1);

      // Construct all sprite URLs using the ia array
      // The ia array contains sprite identifiers like: "1737725222159-sprite-384x216-1"
      // We just need to append ".jpg" to each identifier
      const spriteUrls: string[] = [];

      for (const spriteId of keyframe.ia) {
        // Construct full URL: basePath + spriteId + .jpg + queryParams
        const spriteFileName = `${spriteId}.jpg`;
        const fullUrl = `${basePath}${spriteFileName}${
          queryParams ? '?' + queryParams : ''
        }`;
        spriteUrls.push(fullUrl);
      }

      return spriteUrls;
    } catch (error) {
      console.error('Error generating sprite URLs:', error);
      return [];
    }
  }

  updatePlayerState(playerNetworkState: AppPlayerNetworkState) {
    this.playerState$.next(playerNetworkState);
  }

  isAdEnabled(): boolean {
    const isEnableAds =
      this.configService.applicationConfig.enableAds === 'true';
    if (this.configService.applicationConfig.adBlacklistModels) {
      const isBlockedAds = shouldDisableAds(
        navigator.userAgent,
        this.configService.applicationConfig.adBlacklistModels
      );
      return isEnableAds && !isBlockedAds;
    }
    return isEnableAds; // AHA-7397
  }

  isKeyframesEnabled(): boolean {
    const isEnableKeyframes =
      this.configService.applicationConfig.enableKeyframeV2 !== 'false';
    if (this.configService.applicationConfig.keyframeBlacklistModelsV2) {
      const isBlockedKeyframes = shouldDisableKeyframes(
        navigator.userAgent,
        this.configService.applicationConfig.keyframeBlacklistModelsV2
      );
      return isEnableKeyframes && !isBlockedKeyframes;
    }
    return isEnableKeyframes;
  }

  isDeviceBelow2020(): boolean {
    return isDeviceBelow2020(navigator.userAgent);
  }
}
